import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

import { MembershipV2Service } from 'src/app/services/membership-v2.service';
import { KundeKortService } from 'src/app/services/kunde-kort.service';
import { AuthService } from 'src/app/services/auth.service';
import { NotificationService } from 'src/app/shared/notification.service';

// Global (cross-site) dashboard with two tabs: connect each GROUP to the
// customer (kundekort) that gets billed for it, and connect each SYSTEM to a
// customer via system-kunder (for guest/no-login systems whose subsidy is
// configured on the system itself, e.g. Tide Buss/SAS-style kiosks — there's
// no group to link, so this is the only way to stop them landing under
// "Unassigned"). Self-loading: reads all groups/systems for the company
// (every site) plus customers and a site/company-name map — it does NOT
// depend on a selected site, so it works as a top-level page and equally when
// embedded in the Membership (v2) tabs.
@Component({
  selector: 'customer-mapping',
  templateUrl: './customer-mapping.component.html',
  styleUrls: ['./customer-mapping.component.css']
})
export class CustomerMappingComponent implements OnInit, OnDestroy {

  customers: any[] = [];
  allGroups: any[] = [];                                   // every group, all sites (global)
  sitesMap: { [id: string]: { name: string, companyId: string } } = {};
  companiesMap: { [id: string]: string } = {};

  mapFilterUnassigned = false;
  savingMap: { [dbid: string]: boolean } = {};
  mapSearch = '';                                          // filters the group rows
  customerFilter = '';                                     // filters the customer dropdown

  _isSuperAdmin = false;
  showAllCompanies = false;                                // super-admin only: no companyId filter
  private groupsSub: Subscription;

  // Whether a given row's customer <mat-select> panel is currently open. The
  // full (unfiltered) option list is only rendered while true — with ~100+
  // rows each eagerly mounting one <mat-option> per customer, the page was
  // instantiating tens of thousands of Angular Material components on load
  // and hanging the tab. Gating the list behind "opened" keeps each closed
  // row down to 1-2 options.
  mapSelectOpen: { [dbid: string]: boolean } = {};
  sysSelectOpen: { [systemId: string]: boolean } = {};

  // ---- Systems tab: which systems still need a system-kunder link ---------
  // Standing view (not tied to a specific reconcile month) — every system,
  // whether or not it's had transactions recently. A system with NO link at
  // all is exactly what silently drops its subsidy to "Unassigned" in billing
  // (e.g. Tide Buss/SAS/Brødrene Dahl-style guest-subsidy systems).
  allSystems: any[] = [];
  allSystemLinks: any[] = [];                              // raw system-kunder docs, many-to-many
  sysFilterUnlinked = false;
  savingSys: { [key: string]: boolean } = {};              // keyed by systemId (add) or dbid (remove)
  sysSearch = '';
  sysCustomerFilter: { [systemId: string]: string } = {};  // per-row "add customer" search box
  sysAddSelection: { [systemId: string]: string } = {};    // per-row pending selection in the "add" dropdown
  systemSuggestions: { [systemId: string]: { customerId: string, name: string, score: number } } = {};

  // Fuzzy auto-match: a group's name or site name must be at least this similar
  // (Dice coefficient) to a customer name, and clearly the best candidate.
  private readonly MATCH_THRESHOLD = 0.6;
  private readonly MATCH_MARGIN = 0.05;                    // best must beat runner-up by this
  // dbid -> best customer suggestion (only when it clears the threshold).
  suggestions: { [dbid: string]: { customerId: string, name: string, score: number } } = {};

  private subs: Subscription[] = [];

  constructor(
    private svc: MembershipV2Service,
    private kundeKort: KundeKortService,
    private auth: AuthService,
    private notifier: NotificationService
  ) { }

  ngOnInit() {
    this.subs.push(this.kundeKort.getCustomers().subscribe((c: any[]) => {
      this.customers = c; this.recomputeSuggestions(); this.recomputeSystemSuggestions();
      this.recomputeMappingGroups(); this.recomputeSystemRows();
    }));
    this.svc.getSitesMapOnce().then(m => { this.sitesMap = m; this.recomputeSuggestions(); this.recomputeMappingGroups(); }).catch(() => { });
    this.svc.getCompaniesMapOnce().then(m => { this.companiesMap = m; this.recomputeMappingGroups(); this.recomputeSystemRows(); }).catch(() => { });

    this.subs.push(this.auth.getUser$().subscribe(u => { this._isSuperAdmin = this.auth.isSuperAdmin(u); }));
    this.subscribeGroups();

    this.subs.push(this.kundeKort.getAllSystems$().subscribe((s: any[]) => { this.allSystems = s; this.recomputeSystemSuggestions(); this.recomputeSystemRows(); }));
    this.subs.push(this.kundeKort.getAllSystemKunder$().subscribe((l: any[]) => { this.allSystemLinks = l; this.recomputeSystemSuggestions(); this.recomputeSystemRows(); }));
  }

  ngOnDestroy() { this.subs.forEach(s => s.unsubscribe()); if (this.groupsSub) { this.groupsSub.unsubscribe(); } }

  // Re-subscribes the groups stream to either the current-company or
  // all-companies read, depending on the toggle. Only super admins can flip it.
  private subscribeGroups() {
    if (this.groupsSub) { this.groupsSub.unsubscribe(); }
    const source$ = (this.showAllCompanies && this._isSuperAdmin)
      ? this.svc.getAllGroupsLive$()
      : this.svc.getGroupsByCompany$();
    this.groupsSub = source$.subscribe((rows: any[]) => { this.allGroups = rows; this.recomputeSuggestions(); this.recomputeMappingGroups(); });
  }

  toggleAllCompanies() {
    this.showAllCompanies = !this.showAllCompanies;
    this.subscribeGroups();
  }

  companyName(companyId: string) {
    return this.companiesMap[companyId] || (companyId || '—');
  }

  // ---- lookups -----------------------------------------------------------
  siteName(siteId: string) {
    const s = this.sitesMap[siteId];
    return s ? s.name : (siteId || '—');
  }
  customerName(id: string) {
    const c = this.customers.find(x => x.kundekortId === id);
    return c ? c.name : (id ? '(unknown)' : '—');
  }

  // Field, not a getter: the filter builds a search string per row and calls
  // siteName/customerName/companyName (each an array .find()) for all ~118
  // groups. As a getter that ran on every change-detection pass.
  mappingGroups: any[] = [];

  recomputeMappingGroups() {
    let list = this.allGroups || [];
    if (this.mapFilterUnassigned) { list = list.filter(g => !g.customerId); }
    const q = this.mapSearch.trim().toLowerCase();
    if (q) {
      list = list.filter(g =>
        `${g.name || ''} ${g.groupId || ''} ${this.siteName(g.siteId)} ${this.customerName(g.customerId)} ${this.companyName(g.companyId)}`
          .toLowerCase().includes(q));
    }
    this.mappingGroups = list.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    this.unassignedGroupCount = (this.allGroups || []).filter(g => !g.customerId).length;
    this.autoMatches = (this.allGroups || [])
      .filter(g => !g.customerId && this.suggestions[g.dbid])
      .map(g => ({ group: g, s: this.suggestions[g.dbid] }));
  }

  unassignedGroupCount = 0;
  // Customer options filtered by the in-dropdown search box.
  get filteredCustomers(): any[] {
    const q = this.customerFilter.trim().toLowerCase();
    const list = this.customers || [];
    if (!q) { return list; }
    return list.filter(c => `${c.name || ''} ${c.orgId || ''}`.toLowerCase().includes(q));
  }

  // ---- inline assignment -------------------------------------------------
  // Persist a group -> customer link (no dialog). Optimistically updates the
  // in-memory row so counts/labels refresh immediately; rolls back on failure.
  assignCustomer(g: any, customerId: string) {
    const value = customerId || null;
    if ((g.customerId || null) === value) { return; }
    this.savingMap[g.dbid] = true;
    const prev = g.customerId || null;
    g.customerId = value;
    this.recomputeMappingGroups();
    this.svc.updateGroup(g.dbid, { customerId: value })
      .then(() => this.svc.syncRenewalTask(g.dbid, g))
      .then(() => { this.savingMap[g.dbid] = false; })
      .catch(() => {
        g.customerId = prev;
        this.savingMap[g.dbid] = false;
        this.recomputeMappingGroups();
      });
  }

  // ---- auto-match (fuzzy, >=60% name similarity) -------------------------
  // Strip company suffixes + punctuation/spacing so "Sande Auto AS" and
  // "sande-auto" compare cleanly.
  private normalizeName(s: string): string {
    return String(s || '')
      .toLowerCase()
      .replace(/\b(as|asa|sa|hf|ba|ans|da)\b/g, '')
      .replace(/[^a-z0-9æøå]/g, '');
  }

  // Dice coefficient over character bigrams — a robust 0..1 name-similarity that
  // tolerates word order / minor spelling differences. Short strings fall back
  // to exact equality.
  private similarity(a: string, b: string): number {
    if (!a || !b) { return 0; }
    if (a === b) { return 1; }
    if (a.length < 2 || b.length < 2) { return a === b ? 1 : 0; }
    const bigrams = (s: string) => {
      const m: { [g: string]: number } = {};
      for (let i = 0; i < s.length - 1; i++) { const g = s.substr(i, 2); m[g] = (m[g] || 0) + 1; }
      return m;
    };
    const ma = bigrams(a); const mb = bigrams(b);
    let inter = 0;
    Object.keys(ma).forEach(g => { if (mb[g]) { inter += Math.min(ma[g], mb[g]); } });
    return (2 * inter) / ((a.length - 1) + (b.length - 1));
  }

  // For every unassigned group, find the best-matching customer by comparing the
  // customer name against BOTH the group name and the site name. Keep it only
  // when it clears the threshold AND clearly beats the runner-up (avoids picking
  // one of several near-identical customers at random).
  private recomputeSuggestions() {
    const custs = (this.customers || []).map(c => ({ c, norm: this.normalizeName(c.name) })).filter(x => x.norm);
    const next: { [dbid: string]: { customerId: string, name: string, score: number } } = {};
    (this.allGroups || []).filter(g => !g.customerId).forEach(g => {
      const targets = [this.normalizeName(g.name), this.normalizeName(this.siteName(g.siteId))].filter(Boolean);
      if (!targets.length) { return; }
      let best: any = null; let secondScore = 0;
      custs.forEach(({ c, norm }) => {
        const score = Math.max(...targets.map(t => this.similarity(t, norm)));
        if (!best || score > best.score) { secondScore = best ? best.score : secondScore; best = { c, score }; }
        else if (score > secondScore) { secondScore = score; }
      });
      if (best && best.score >= this.MATCH_THRESHOLD && (best.score - secondScore) >= this.MATCH_MARGIN) {
        next[g.dbid] = { customerId: best.c.kundekortId, name: best.c.name, score: best.score };
      }
    });
    this.suggestions = next;
  }

  suggestionFor(g: any) { return this.suggestions[g.dbid] || null; }
  scorePct(score: number) { return Math.round(score * 100); }

  // Filled by recomputeMappingGroups(). It builds new {group, s} objects, and
  // it is bound twice in the template, so as a getter it re-ran constantly.
  autoMatches: { group: any, s: { customerId: string, name: string, score: number } }[] = [];

  applySuggestion(g: any) {
    const s = this.suggestions[g.dbid];
    if (s) { this.assignCustomer(g, s.customerId); }
  }

  deleteGroup(g: any) {
    if (confirm(`Remove group "${g.name}"? The record is kept (soft delete), not erased.`)) {
      this.svc.softDeleteGroup(g.dbid);
    }
  }

  runAutoMatch() {
    const matches = this.autoMatches;
    if (!matches.length) { return; }
    const ok = confirm(
      `Auto-assign ${matches.length} group(s) to the customer whose name is at least ` +
      `${Math.round(this.MATCH_THRESHOLD * 100)}% similar (by group or site name)?\n` +
      `Ambiguous matches are skipped — review or undo any afterwards via the dropdown.`
    );
    if (!ok) { return; }
    matches.forEach(m => this.assignCustomer(m.group, m.s.customerId));
  }

  // ---- Systems tab ---------------------------------------------------------
  // Joins allSystems + allSystemLinks (system-kunder is many-to-many, so a
  // system can carry more than one link — shown as removable chips rather than
  // a single dropdown, same as reconcileSettlements.js treats >1 links as
  // "shared, needs manual split" instead of guessing).
  // Rebuilt by recomputeSystemRows(), never a getter.
  //
  // This is the page's historical crash: the getter below used to run .map()
  // into BRAND-NEW row objects on every change-detection pass. *ngFor tracks
  // items by identity, so all ~95 rows were destroyed and recreated each pass,
  // and every recreated <mat-select> re-registered its options and scheduled
  // another pass — an unbounded loop that froze the tab. A field computed on
  // demand hands ngFor the same object references, so it does nothing.
  systemRows: any[] = [];

  recomputeSystemRows() {
    const linksBySystem: { [systemId: string]: any[] } = {};
    (this.allSystemLinks || []).forEach(l => {
      if (!l.systemId) { return; }
      (linksBySystem[l.systemId] || (linksBySystem[l.systemId] = [])).push(l);
    });
    let list = (this.allSystems || []).map(s => ({
      ...s,
      subsidyPct: Number(s.subsidy) || 0,
      isGuestSubsidy: String(s.needLogin) === 'false' && Number(s.subsidy) > 0,
      links: linksBySystem[s.systemId] || []
    }));
    if (this.sysFilterUnlinked) { list = list.filter(s => !s.links.length); }
    const q = this.sysSearch.trim().toLowerCase();
    if (q) {
      list = list.filter(s => {
        const customerNames = s.links.map((l: any) => this.customerName(l.kundekortId)).join(' ');
        return `${s.name || ''} ${s.systemId || ''} ${this.companyName(s.companyId)} ${customerNames}`.toLowerCase().includes(q);
      });
    }
    this.systemRows = list.sort((a, b) => {
      // Unlinked-with-guest-subsidy first (the ones that actually cause silent
      // billing gaps), then unlinked, then linked — alphabetical within each.
      const rank = (s: any) => s.links.length ? 2 : (s.isGuestSubsidy ? 0 : 1);
      return (rank(a) - rank(b)) || (a.name || '').localeCompare(b.name || '');
    });
    const linked = new Set((this.allSystemLinks || []).map(l => l.systemId));
    this.unlinkedSystemCount = (this.allSystems || []).filter(s => !linked.has(s.systemId)).length;
    this.systemAutoMatches = this.systemRows
      .filter(s => !s.links.length && this.systemSuggestions[s.systemId])
      .map(s => ({ system: s, s: this.systemSuggestions[s.systemId] }));
  }

  unlinkedSystemCount = 0;

  filteredCustomersFor(systemId: string): any[] {
    const q = (this.sysCustomerFilter[systemId] || '').trim().toLowerCase();
    const list = this.customers || [];
    if (!q) { return list; }
    return list.filter(c => `${c.name || ''} ${c.orgId || ''}`.toLowerCase().includes(q));
  }

  addSystemLink(system: any, customerId: string) {
    if (!customerId) { return; }
    if (!system || !system.systemId) {
      this.notifier.showNotification('error', 'This system has no system ID — cannot link it.');
      return;
    }
    if (system.links.some((l: any) => l.kundekortId === customerId)) {
      this.notifier.showNotification('error', `${this.customerName(customerId)} is already linked to this system.`);
      return;
    }
    this.savingSys[system.systemId] = true;
    this.kundeKort.addSystemKunde(system.systemId, customerId)
      .then(() => {
        this.savingSys[system.systemId] = false;
        this.sysAddSelection[system.systemId] = '';
        this.notifier.showNotification('success', `Linked ${this.customerName(customerId)} to ${system.name || system.systemId}`);
      })
      .catch((err: any) => {
        this.savingSys[system.systemId] = false;
        // A silent failure here is indistinguishable from the control not
        // working at all, which is exactly how this was reported.
        console.error('addSystemKunde failed', err);
        this.notifier.showNotification('error', 'Could not link the customer: ' + ((err && err.message) || 'unknown error'));
      });
  }

  removeSystemLink(link: any) {
    this.savingSys[link.dbid] = true;
    this.kundeKort.removeSystemKunde(link.dbid)
      .then(() => {
        this.savingSys[link.dbid] = false;
        this.notifier.showNotification('success', 'Link removed');
      })
      .catch((err: any) => {
        this.savingSys[link.dbid] = false;
        console.error('removeSystemKunde failed', err);
        this.notifier.showNotification('error', 'Could not remove the link: ' + ((err && err.message) || 'unknown error'));
      });
  }

  // Fuzzy auto-match for fully-unlinked systems only (same Dice-coefficient
  // approach as groups) — a shared/ambiguous system already has a link and
  // isn't a candidate here.
  private recomputeSystemSuggestions() {
    const custs = (this.customers || []).map(c => ({ c, norm: this.normalizeName(c.name) })).filter(x => x.norm);
    const linked = new Set((this.allSystemLinks || []).map(l => l.systemId));
    const next: { [systemId: string]: { customerId: string, name: string, score: number } } = {};
    (this.allSystems || []).filter(s => !linked.has(s.systemId)).forEach(s => {
      const target = this.normalizeName(s.name);
      if (!target) { return; }
      let best: any = null; let secondScore = 0;
      custs.forEach(({ c, norm }) => {
        const score = this.similarity(target, norm);
        if (!best || score > best.score) { secondScore = best ? best.score : secondScore; best = { c, score }; }
        else if (score > secondScore) { secondScore = score; }
      });
      if (best && best.score >= this.MATCH_THRESHOLD && (best.score - secondScore) >= this.MATCH_MARGIN) {
        next[s.systemId] = { customerId: best.c.kundekortId, name: best.c.name, score: best.score };
      }
    });
    this.systemSuggestions = next;
  }

  suggestionForSystem(s: any) { return this.systemSuggestions[s.systemId] || null; }

  // Filled by recomputeSystemRows() — it derives from systemRows.
  systemAutoMatches: { system: any, s: { customerId: string, name: string, score: number } }[] = [];


  applySystemSuggestion(system: any) {
    const s = this.systemSuggestions[system.systemId];
    if (s) { this.addSystemLink(system, s.customerId); }
  }

  runSystemAutoMatch() {
    const matches = this.systemAutoMatches;
    if (!matches.length) { return; }
    const ok = confirm(
      `Link ${matches.length} system(s) to the customer whose name is at least ` +
      `${Math.round(this.MATCH_THRESHOLD * 100)}% similar to the system name?\n` +
      `Ambiguous matches are skipped — review or undo any afterwards.`
    );
    if (!ok) { return; }
    matches.forEach(m => this.addSystemLink(m.system, m.s.customerId));
  }
}
